// src/components/Login.js
import React, { useState, useEffect } from "react";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ResetPassword from "./ResetPassword";

const auth = getAuth();
const db = getFirestore();

// Store login attempt count and lockout time
const loginAttempts = {};
const MAX_ATTEMPTS = 5;
const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes in milliseconds

function Login({ onClose = null }) {
  const location = useLocation();
  // Check if we were redirected with an error message
  const [redirectMessage, setRedirectMessage] = useState(
    location.state?.message || location.state?.error || ""
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for stored email if remember me was used
    const storedEmail = localStorage.getItem("rememberedEmail");
    if (storedEmail) {
      setEmail(storedEmail);
      setRememberMe(true);
    }

    // Clear any redirect message when component unmounts
    return () => {
      if (location.state?.message || location.state?.error) {
        window.history.replaceState({}, document.title);
      }
    };
  }, [location]);
  
  // Add this to prevent browser password dialogs
  useEffect(() => {
    // Insert a hidden login form to prevent browser password notifications
    const hiddenForm = document.createElement('form');
    hiddenForm.style.display = 'none';
    hiddenForm.id = 'hidden-login-form';
    hiddenForm.setAttribute('autocomplete', 'off');
    document.body.appendChild(hiddenForm);
    
    return () => {
      if (document.getElementById('hidden-login-form')) {
        document.body.removeChild(hiddenForm);
      }
    };
  }, []);

  useEffect(() => {
    if (auth.currentUser) {
      navigate("/dashboard");
    }
  }, [navigate]);

  // Handle countdown timer for rate limiting
  useEffect(() => {
    if (!lockedUntil) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      if (now >= lockedUntil) {
        setLockedUntil(null);
        clearInterval(interval);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Check if account is locked due to too many attempts
    const now = Date.now();
    if (loginAttempts[email]?.lockedUntil && loginAttempts[email].lockedUntil > now) {
      const remainingSeconds = Math.ceil((loginAttempts[email].lockedUntil - now) / 1000);
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      setLockedUntil(loginAttempts[email].lockedUntil);
      setIsLoading(false);
      setError(`Too many login attempts. Try again in ${minutes}m ${seconds}s.`);
      return;
    }

    try {
      // Import the log auth event function
      const { logAuthEvent } = await import('../firebase');
      
      // Log login attempt
      logAuthEvent('login_attempt', { email });
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Reset login attempts for this email on successful login
      if (loginAttempts[email]) {
        delete loginAttempts[email];
      }

      // Check if email is verified (uncomment when ready to enforce)
      // if (!user.emailVerified) {
      //   await auth.signOut();
      //   setIsLoading(false);
      //   setError("Please verify your email before logging in. Check your inbox for a verification link.");
      //   
      //   // Log failed login due to unverified email
      //   logAuthEvent('login_failed', { 
      //     email, 
      //     reason: 'email_not_verified'
      //   });
      //   
      //   return;
      // }

      // Store email in localStorage if remember me is checked
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      if (!auth.currentUser) {
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      
      // Log successful login
      logAuthEvent('login_success', { 
        email, 
        userId: user.uid,
        emailVerified: user.emailVerified
      });

      const userDocRef = doc(db, "users", user.uid);
      const userDocSnapshot = await getDoc(userDocRef);

      if (!userDocSnapshot.exists()) {
        throw new Error("User account not found. Please contact administrator.");
      }

      const userData = userDocSnapshot.data();

      // Normalize status and check it
      const status = userData.status ? userData.status.toString().toLowerCase() : null;
      console.log("User status from database:", userData.status, "Normalized to:", status);

      if (status !== "enable" && status !== "enabled") {
        throw new Error("Your account has been disabled. Please contact administrator.");
      }

      const allowedRoles = ["admin", "user"];
      if (!userData.role || !allowedRoles.includes(userData.role)) {
        throw new Error("Access denied. Your account does not have sufficient privileges.");
      }

      if (!userData.updatedAt || new Date() - new Date(userData.updatedAt) > 60000) {
        await updateDoc(userDocRef, { updatedAt: new Date().toISOString() });
      }

      navigate("/dashboard");
      onClose();
    } catch (err) {
      console.error("🔥 Login error:", err.message);
      
      // Handle rate limiting
      if (!loginAttempts[email]) {
        loginAttempts[email] = { count: 0 };
      }
      
      loginAttempts[email].count += 1;
      
      if (loginAttempts[email].count >= MAX_ATTEMPTS) {
        const lockoutTime = Date.now() + LOCKOUT_TIME;
        loginAttempts[email].lockedUntil = lockoutTime;
        setLockedUntil(lockoutTime);
        setError(`Too many login attempts. Account locked for 15 minutes.`);
      } else {
        // Return more user-friendly error messages
        let errorMessage = "Login failed. Please check your credentials and try again.";
        
        if (err.code === "auth/user-not-found") {
          errorMessage = "No account found with this email. Please check your email or register.";
        } else if (err.code === "auth/wrong-password") {
          errorMessage = "Incorrect password. Please try again.";
        } else if (err.code === "auth/too-many-requests") {
          errorMessage = "Access temporarily disabled due to many failed login attempts. Try again later.";
        } else if (err.code === "auth/network-request-failed") {
          errorMessage = "Network error. Please check your internet connection.";
        } else if (err.message.includes("disabled")) {
          errorMessage = err.message;
        } else if (err.message.includes("access denied")) {
          errorMessage = err.message;
        } else if (err.message.includes("not found")) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3 } },
  };
  
  // Using the location from the hook declared at the top of the component
  const isStandalonePage = location.pathname === '/login';

  // If this is the standalone login page, render a full page login form
  if (isStandalonePage) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
          <div>
            <img className="mx-auto h-12 w-auto" src="/assets/medpredix-logo-black.svg" alt="Medpredix Logo" />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
          </div>
          {!showResetPassword ? (
            <>
              <form onSubmit={handleLogin} className="mt-8 space-y-6" autoComplete="off">
                <div className="relative">
                  <input
                    type="email"
                    name="email" 
                    autoComplete="new-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full p-3 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-black placeholder-gray-400"
                    required
                    disabled={isLoading || lockedUntil}
                  />
                </div>
                <div className="relative">
                  <input
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full p-3 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-black placeholder-gray-400"
                    required
                    disabled={isLoading || lockedUntil}
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    disabled={isLoading || lockedUntil}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div>
                <button
                  type="submit"
                  className={`w-full ${isLoading || lockedUntil ? 'bg-purple-400' : 'bg-purple-600 hover:bg-purple-700'} text-white p-3 rounded-lg transition-colors duration-300 font-semibold`}
                  disabled={isLoading || lockedUntil}
                >
                  {isLoading ? "Logging in..." : "Login"}
                </button>
                {(error || redirectMessage) && <p className="text-red-500 mt-2 text-sm">{error || redirectMessage}</p>}
              </form>
              <button
                onClick={() => setShowResetPassword(true)}
                className="mt-3 text-purple-600 hover:underline text-sm"
                disabled={isLoading || lockedUntil}
              >
                Forgot Password?
              </button>
            </>
          ) : (
            <ResetPassword onClose={() => setShowResetPassword(false)} />
          )}
        </div>
      </div>
    );
  }
  
  // Modal version for popup use
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={modalVariants}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-2xl font-bold"
        >
          ×
        </button>

        {!showResetPassword ? (
          <>
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Login</h2>
            <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
              <div className="relative">
                <input
                  type="email"
                  name="email" 
                  autoComplete="new-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full p-3 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-black placeholder-gray-400"
                  required
                  disabled={isLoading || lockedUntil}
                />
              </div>
              <div className="relative">
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full p-3 border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 text-black placeholder-gray-400"
                  required
                  disabled={isLoading || lockedUntil}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  disabled={isLoading || lockedUntil}
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <button
                type="submit"
                className={`w-full ${isLoading || lockedUntil ? 'bg-purple-400' : 'bg-purple-600 hover:bg-purple-700'} text-white p-3 rounded-lg transition-colors duration-300 font-semibold`}
                disabled={isLoading || lockedUntil}
              >
                {isLoading ? "Logging in..." : "Login"}
              </button>
              {(error || redirectMessage) && <p className="text-red-500 mt-2 text-sm">{error || redirectMessage}</p>}
            </form>

            <button
              onClick={() => setShowResetPassword(true)}
              className="mt-3 text-purple-600 hover:underline text-sm"
              disabled={isLoading || lockedUntil}
            >
              Forgot Password?
            </button>
          </>
        ) : (
          <ResetPassword onClose={() => setShowResetPassword(false)} />
        )}
      </motion.div>
    </motion.div>
  );
}

export default Login;